import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Phone, Search, MessageSquarePlus, Filter, FileCheck, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PREDICTION_OPTIONS, PREDICTION_LABELS, PREDICTION_LABELS_NUM, COLORS, STATUS_LABELS, STATUS_OPTIONS, VOTE_2022_OPTIONS, VOTE_2022_LABELS, BRANCHES } from "@/lib/constants";
import { PredictionBadge, StatusBadge } from "@/components/badges";
import VoterEditDialog from "@/components/VoterEditDialog";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ALL = "__all__";

export default function CallAssistant() {
  const { committee, user } = useAuth();
  const [voters, setVoters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState([]);
  const [branches, setBranches] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [search, setSearch] = useState("");
  const [editId, setEditId] = useState(null);
  const [filters, setFilters] = useState({
    status: ALL, prediction: ALL, vote_2022: ALL, caller: ALL,
    branch: ALL, sector: ALL, proxy: ALL, ito: ALL,
  });
  const canManage = ["super_admin", "committee_manager", "branch_manager"].includes(user.role);
  const activeCount = Object.values(filters).filter((v) => v !== ALL).length + (search ? 1 : 0);
  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const clearFilters = () => {
    setFilters({ status: ALL, prediction: ALL, vote_2022: ALL, caller: ALL, branch: ALL, sector: ALL, proxy: ALL, ito: ALL });
    setSearch("");
  };

  useEffect(() => {
    api.get("/meta").then(({ data }) => {
      setTeam(data.team_members || []);
      setBranches(data.branches || []);
      if (data.sectors) setSectors(data.sectors);
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (committee !== "all" && committee != null) params.committee_no = committee;
    if (user.role === "chairman") params.caller = user.agent_name;
    if (search) params.search = search;
    Object.entries(filters).forEach(([k, v]) => { if (v !== ALL) params[k] = v; });
    try {
      const { data } = await api.get("/voters", { params });
      setVoters(data);
    } finally {
      setLoading(false);
    }
  }, [committee, user.role, user.agent_name, search, filters]);

  useEffect(() => {
    if (committee === null) return;
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load, committee]);

  const quickSet = async (v, prediction) => {
    if (prediction === "KARSI" && v.current_vote_prediction_2026 === "KESIN") {
      toast.error("Kesin oy karşıya dönüyor: sebebi girmek için firma kartını açın");
      setEditId?.(v.id);
      return;
    }
    try {
      await api.patch(`/voters/${v.id}`, { current_vote_prediction_2026: prediction });
      setVoters((list) => list.map((x) => x.id === v.id ? { ...x, current_vote_prediction_2026: prediction } : x));
      toast.success(`${v.company_name.slice(0, 22)}... → ${PREDICTION_LABELS[prediction]}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const quickProxy = async (v) => {
    const next = !v.proxy_document;
    try {
      await api.patch(`/voters/${v.id}`, { proxy_document: next });
      setVoters((list) => list.map((x) => x.id === v.id ? { ...x, proxy_document: next } : x));
      toast.success(next ? "Yetki belgesi alındı olarak işaretlendi" : "Yetki belgesi işareti kaldırıldı");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const FilterSelect = ({ k, placeholder, options, labels }) => (
    <Select value={filters[k]} onValueChange={(v) => setF(k, v)}>
      <SelectTrigger className="h-9 w-full" data-testid={`call-filter-${k}`}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className="max-h-72">
        <SelectItem value={ALL}>{placeholder}: Tümü</SelectItem>
        {(options || []).map((o) => <SelectItem key={o} value={o}>{labels ? labels[o] : o}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  return (
    <div className="space-y-4 max-w-3xl mx-auto" data-testid="call-assistant-page">
      <div>
        <h1 className="font-display text-2xl font-bold text-foreground">Arama Asistanı</h1>
        <p className="text-sm text-muted-foreground">Saha aramaları için hızlı durum güncelleme</p>
      </div>

      <Card className="p-3 border border-border space-y-2 sticky top-16 z-30 bg-card">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Firma adı, sicil no veya kayıt yılı ara..." className="pl-9" data-testid="call-search" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <FilterSelect k="status" placeholder="Durum" options={STATUS_OPTIONS} labels={STATUS_LABELS} />
          <FilterSelect k="prediction" placeholder="2026 Tahmin" options={PREDICTION_OPTIONS} labels={PREDICTION_LABELS_NUM} />
          <FilterSelect k="vote_2022" placeholder="2022 Sonuç" options={VOTE_2022_OPTIONS} labels={VOTE_2022_LABELS} />
          {canManage && <FilterSelect k="caller" placeholder="Sorumlu" options={team} />}
          <FilterSelect k="branch" placeholder="Şube" options={branches.length ? branches : BRANCHES} />
          <FilterSelect k="sector" placeholder="Sektör" options={sectors} />
          <FilterSelect k="proxy" placeholder="Yetki Belgesi" options={["yes", "no"]} labels={{ yes: "Belgeli", no: "Belgesiz" }} />
          <FilterSelect k="ito" placeholder="İTO Sicil No" options={["yes", "no"]} labels={{ yes: "İTO No Var", no: "İTO No Eksik" }} />
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Filter className="h-4 w-4 text-cyan-600" />
          <span className="text-muted-foreground" data-testid="call-result-count">
            {activeCount > 0 ? `${activeCount} filtre aktif · ` : ""}{voters.length} firma
          </span>
          {activeCount > 0 && (
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={clearFilters} data-testid="call-clear-filters">
              <X className="h-3 w-3 mr-1" />Temizle
            </Button>
          )}
        </div>
      </Card>

      {loading ? (
        <div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-40 rounded-xl" />)}</div>
      ) : voters.length === 0 ? (
        <p className="text-center py-16 text-muted-foreground">Aranacak firma bulunamadı</p>
      ) : (
        <div className="space-y-3">
          {voters.map((v) => (
            <Card key={v.id} className="p-4 border border-border" data-testid={`call-card-${v.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold text-foreground leading-tight">{v.company_name}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {v.contact_person || "Yetkili yok"} &bull; {v.branch}
                  </p>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <StatusBadge value={v.status} />
                    <PredictionBadge value={v.current_vote_prediction_2026} />
                    <button onClick={() => quickProxy(v)} data-testid={`proxy-toggle-${v.id}`}
                      className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full border transition-colors ${v.proxy_document ? "bg-emerald-600 text-white border-emerald-600" : "bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-900"}`}>
                      <FileCheck className="h-3.5 w-3.5" /> Yetki Belgesi
                    </button>
                  </div>
                </div>
                <Button asChild size="lg" className="h-14 w-14 rounded-full bg-emerald-600 hover:bg-emerald-700 shrink-0" data-testid={`call-btn-${v.id}`}>
                  <a href={`tel:${(v.phone || "").replace(/\s/g, "")}`}><Phone className="h-6 w-6" /></a>
                </Button>
              </div>

              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-4">
                {PREDICTION_OPTIONS.map((p) => {
                  const active = v.current_vote_prediction_2026 === p;
                  const c = COLORS[p];
                  return (
                    <button key={p} onClick={() => quickSet(v, p)} data-testid={`quick-${p}-${v.id}`}
                      className="text-xs font-semibold py-2.5 rounded-lg border transition-colors"
                      style={active
                        ? { background: c, color: "#fff", borderColor: c }
                        : { background: `${c}14`, color: c, borderColor: `${c}44` }}>
                      {PREDICTION_LABELS_NUM[p]}
                    </button>
                  );
                })}
              </div>

              <Button variant="outline" className="w-full mt-3 h-10" onClick={() => setEditId(v.id)} data-testid={`note-btn-${v.id}`}>
                <MessageSquarePlus className="h-4 w-4 mr-2" />
                Not Ekle & Detay ({(v.notes_2026 || []).length})
              </Button>
            </Card>
          ))}
        </div>
      )}

      <VoterEditDialog voterId={editId} open={!!editId} onClose={() => setEditId(null)} onSaved={() => load()} team={team} />
    </div>
  );
}
